import React, { useState, useEffect, useMemo } from 'react';
import { shortLocation } from '../../../utils/locationDisplay';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  message,
  Typography,
  Row,
  Col,
  Statistic,
  Drawer,
  Descriptions,
  Tooltip,
  Avatar,
  Steps,
  Dropdown,
  Menu,
  Checkbox,
  Popconfirm,
} from 'antd';
import {
  SwapOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  HomeOutlined,
  ArrowRightOutlined,
  BoxPlotOutlined,
  WarningOutlined,
  SettingOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  UpOutlined,
  DownOutlined,
} from '@ant-design/icons';
// import type { ColumnsType } from 'antd/es/table';
import useDraggableColumns from '../../../components/table/useDraggableColumns';
import dayjs from 'dayjs';
import InternalTransferForm from '../components/InternalTransferForm';
import apiService from '../../../services/apiService';
import { useTranslation } from "react-i18next";
import { useStatusLabel } from '../../../i18n/statusLabel';

const { Title, Text } = Typography;
const { Option } = Select;
const { Step } = Steps;

// 轉移狀態類型（精簡版）
export type TransferStatus = 
  | '待審批'
  | '已批準'
  | '已拒絕';

// 轉移項目接口
export interface TransferItem {
  id: string;
  productId: string;
  productName: string;
  productNameCN: string;
  sku: string;
  quantity: number;
  availableQuantity: number;
  unit: string;
  fromLocation: string;
  // Composite "<locationId>::<lotNumber>" identity of the selected source row.
  // One location can hold several lots of the same SKU, so locationId alone
  // cannot say which row was picked. UI-only: the API still receives fromLocation.
  fromLocationKey?: string;
  toLocation: string;
  remark?: string;
  isQuantityValid: boolean;
  lotNumber?: string;        // Source lot number (read-only, from source location)
  expiryDate?: string;       // Source expiry date (read-only, from source location)
  targetLotNumber?: string;  // Target lot number (editable, for destination)
  targetExpiryDate?: string; // Target expiry date (editable, for destination)
}

// 內部轉移請求接口
export interface InternalTransfer {
  id: string;
  transferNumber: string;
  title: string;
  fromWarehouse: string;
  toWarehouse: string;
  requestedBy: string;
  requestedDate: string;
  approvedBy?: string;
  approvedDate?: string;
  executedBy?: string;
  executedDate?: string;
  status: TransferStatus;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  reason: string;
  items: TransferItem[];
  totalItems: number;
  totalQuantity: number;
  estimatedValue: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// 倉庫數據接口
interface Warehouse {
  value: string; // prefer id
  label: string;
  locations: string[];
  id?: string;
  code?: string;
}

// 客戶數據接口
interface Customer {
  id: string;
  name: string;
  code: string;
}

// 產品數據接口
interface Product {
  id: string;
  name: string;
  nameCN: string;
  sku: string;
  availableStock: Record<string, number>;
  unit: string;
  currentLocation: {
    warehouse: string;
    location: string;
    customer: string;
  };
}

const InternalTransferPage: React.FC = () => {
    const { t } = useTranslation();
    const statusLabel = useStatusLabel();
  const [transfers, setTransfers] = useState<InternalTransfer[]>([]);
  const [filteredTransfers, setFilteredTransfers] = useState<InternalTransfer[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState<InternalTransfer | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState<InternalTransfer | null>(null);

  // 倉庫、客戶、產品數據狀態
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);

  // 排序狀態
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | ''>('');

  // 列配置狀態
  const [columnSettings, setColumnSettings] = useState(() => {
    const saved = localStorage.getItem('internal-transfer-columns-config');
    const version = localStorage.getItem('internal-transfer-columns-version');
    
    if (version !== '2024-01-v2' || !saved) {
      const defaultConfig = {
        transferNumber: { visible: true, order: 0 },
        title: { visible: true, order: 1 },
        direction: { visible: true, order: 2 },
        items: { visible: true, order: 3 },
        priority: { visible: true, order: 4 },
        status: { visible: true, order: 5 },
        requestedBy: { visible: true, order: 6 },
        actions: { visible: true, order: 7 }
      };
      localStorage.setItem('internal-transfer-columns-config', JSON.stringify(defaultConfig));
      localStorage.setItem('internal-transfer-columns-version', '2024-01-v2');
      return defaultConfig;
    }
    
    try {
      return JSON.parse(saved);
    } catch {
      const defaultConfig = {
        transferNumber: { visible: true, order: 0 },
        title: { visible: true, order: 1 },
        direction: { visible: true, order: 2 },
        items: { visible: true, order: 3 },
        priority: { visible: true, order: 4 },
        status: { visible: true, order: 5 },
        requestedBy: { visible: true, order: 6 },
        actions: { visible: true, order: 7 }
      };
      return defaultConfig;
    }
  });

  // 篩選狀態
  const [filters, setFilters] = useState({
    status: '',
    warehouse: '',
    priority: '',
    searchText: ''
  });

  // 列設置下拉菜單可見狀態
  const [columnSettingsVisible, setColumnSettingsVisible] = useState(false);

  // 展開收起控制
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const toggleCollapsed = () => setCollapsed((prev: boolean) => !prev);

  // 數據加載函數
  const fetchTransfers = async () => {
    try {
      setLoading(true);
      const resp: any = await apiService.getInternalTransfers({
        status: filters.status || undefined,
        warehouse: filters.warehouse || undefined,
        search: filters.searchText || undefined,
      });
      
      // 兼容不同後端字段命名
      const normalized: InternalTransfer[] = (Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : []).map((t: any) => ({
        id: String(t.id || t._id || t.transferId),
        transferNumber: t.transferNumber || t.number || t.code || '-',
        title: t.title || t.subject || '-',
        fromWarehouse: t.fromWarehouse || t.sourceWarehouse || t.from || '-',
        toWarehouse: t.toWarehouse || t.targetWarehouse || t.to || '-',
        requestedBy: t.requestedBy || t.applicant || t.createdBy || '-',
        requestedDate: t.requestedDate || t.createdAt || new Date().toISOString(),
        approvedBy: t.approvedBy,
        approvedDate: t.approvedDate,
        executedBy: t.executedBy,
        executedDate: t.executedDate,
        status: (t.status as TransferStatus) || '待審批',
        priority: (t.priority as any) || 'medium',
        reason: t.reason || t.remark || '-',
        items: (t.items || []).map((it: any, idx: number) => ({
          id: String(it.id || idx),
          productId: String(it.productId || it.product?.id || idx),
          productName: it.productName || it.product?.name || '-',
          productNameCN: it.productNameCN || it.product?.nameCN || '-',
          sku: it.sku || it.productSku || '-',
          quantity: Number(it.quantity) || 0,
          availableQuantity: Number(it.availableQuantity) || 0,
          unit: it.unit || '個',
          fromLocation: it.fromLocation || '-',
          toLocation: it.toLocation || '-',
          isQuantityValid: it.isQuantityValid !== false,
          // 供編輯頁回顯
          targetCustomer: it.targetCustomer || it.targetCustomerId || '',
          targetWarehouse: it.targetWarehouse || t.toWarehouse || '',
          remark: it.remark ?? '',
          // Lot number and expiry date fields
          lotNumber: it.lotNumber || '',
          expiryDate: it.expiryDate || '',
          targetLotNumber: it.targetLotNumber || '',
          targetExpiryDate: it.targetExpiryDate || '',
        })),
        totalItems: Number(t.totalItems) || (Array.isArray(t.items) ? t.items.length : 0),
        totalQuantity: Number(t.totalQuantity) || 0,
        estimatedValue: Number(t.estimatedValue) || 0,
        notes: t.notes,
        createdAt: t.createdAt || new Date().toISOString(),
        updatedAt: t.updatedAt || new Date().toISOString(),
      }));
      setTransfers(normalized);
    } catch (e) {
      console.error('加載內部轉移數據失敗', e);
      message.error(t('加載內部轉移數據失敗'));
    } finally {
      setLoading(false);
    }
  };

  // 加載倉庫數據
  const fetchWarehouses = async () => {
    try {
      const companyId = localStorage.getItem('companyId') || localStorage.getItem('currentCompanyId') || undefined;
      const response = await apiService.getWarehouses(companyId ? { companyId } : undefined);
      const warehouseData = Array.isArray((response as any)?.data) ? (response as any).data : Array.isArray(response) ? response : [];
      const normalized: Warehouse[] = warehouseData.map((w: any) => ({
        value: String(w.id || w.warehouseId || w.code),
        label: w.name || w.warehouseName || w.label || '-',
        locations: Array.isArray(w.locations) ? w.locations : [],
        id: String(w.id || w.warehouseId || ''),
        code: w.code || w.warehouseCode || undefined,
      }));
      setWarehouses(normalized);
    } catch (e) {
      console.error('加載倉庫數據失敗', e);
      message.error(t('加載倉庫數據失敗'));
    }
  };

  // 加載客戶數據
  const fetchCustomers = async () => {
    try {
      const response = await apiService.getCustomersV2();
      const raw = response as any;
      const customerData = Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw?.customers)
        ? raw.customers
        : Array.isArray(raw)
        ? raw
        : [];
      const normalized: Customer[] = customerData.map((c: any) => ({
        id: String(c.id || c.customerId),
        name: c.name || c.customerName || '-',
        code: c.code || c.customerCode || '-'
      }));
      setCustomers(normalized);
    } catch (e) {
      console.error('加載客戶數據失敗', e);
      message.error(t('加載客戶數據失敗'));
    }
  };

  // 加載產品數據
  const fetchProducts = async () => {
    try {
      const companyId = localStorage.getItem('companyId') || localStorage.getItem('currentCompanyId') || undefined;
      let response: any;
      try {
        response = await apiService.getProductsV2(companyId ? { companyId } : undefined);
      } catch (_) {
        response = await apiService.getProducts(companyId ? { companyId } : undefined);
      }
      const productData = Array.isArray((response as any)?.data)
        ? (response as any).data
        : Array.isArray((response as any)?.products)
        ? (response as any).products
        : Array.isArray(response)
        ? response
        : [];
      const normalized: Product[] = productData.map((p: any) => ({
        id: String(p.id || p.productId || p.itemId || p.code || p.itemCode),
        name: p.name || p.productName || p.itemName || '-',
        nameCN: p.nameCN || p.productNameCN || p.cnName || p.itemName || '-',
        sku: p.sku || p.productSku || p.itemCode || p.code || '-',
        availableStock: p.availableStock || {},
        unit: p.unit || p.uom || '個',
        currentLocation: {
          warehouse: p.currentLocation?.warehouse || '-',
          location: p.currentLocation?.location || '-',
          customer: p.currentLocation?.customer || '-'
        }
      }));
      setProducts(normalized);
    } catch (e) {
      console.error('加載產品數據失敗', e);
      message.error(t('加載產品數據失敗'));
    }
  };

  // 初始化載入所有數據
  useEffect(() => {
    fetchTransfers();
    fetchWarehouses();
    fetchCustomers();
    fetchProducts();
  }, []);

  // 當篩選條件變化時重新加載轉移數據
  useEffect(() => {
    fetchTransfers();
  }, [filters.status, filters.warehouse, filters.searchText]);

  // 統計數據
  const statistics = React.useMemo(() => {
    const total = filteredTransfers.length;
    const pending = filteredTransfers.filter((t: InternalTransfer) => t.status === '待審批').length;
    const approved = filteredTransfers.filter((t: InternalTransfer) => t.status === '已批準').length;
    const rejected = filteredTransfers.filter((t: InternalTransfer) => t.status === '已拒絕').length;
    return { total, pending, approved, rejected };
  }, [filteredTransfers]);

  // 處理列排序
  const handleColumnSort = (columnKey: string) => {
    if (sortField === columnKey) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : sortOrder === 'desc' ? '' : 'asc');
      if (sortOrder === 'desc') {
        setSortField('');
      }
    } else {
      setSortField(columnKey);
      setSortOrder('asc');
    }
  };

  // 獲取排序圖標
  const getSortIcon = (columnKey: string) => {
    if (sortField !== columnKey) return null;
    return sortOrder === 'asc' ? 
      <SortAscendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} /> :
      <SortDescendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} />;
  };

  // 處理列可見性切換
  const handleColumnVisibilityChange = (columnKey: string, visible: boolean) => {
    const newSettings = {
      ...columnSettings,
      [columnKey]: { ...columnSettings[columnKey], visible }
    };
    setColumnSettings(newSettings);
    localStorage.setItem('internal-transfer-columns-config', JSON.stringify(newSettings));
    localStorage.setItem('internal-transfer-columns-version', '2024-01-v2');
  };

  // 應用篩選
  useEffect(() => {
    let filtered = transfers;

    if (filters.status) {
      filtered = filtered.filter((transfer: InternalTransfer) => transfer.status === filters.status);
    }

    if (filters.warehouse) {
      filtered = filtered.filter((transfer: InternalTransfer) => 
        transfer.fromWarehouse === filters.warehouse || transfer.toWarehouse === filters.warehouse
      );
    }

    if (filters.priority) {
      filtered = filtered.filter((transfer: InternalTransfer) => transfer.priority === filters.priority);
    }

    if (filters.searchText) {
      filtered = filtered.filter((transfer: InternalTransfer) =>
        transfer.transferNumber.toLowerCase().includes(filters.searchText.toLowerCase()) ||
        transfer.title.toLowerCase().includes(filters.searchText.toLowerCase()) ||
        transfer.reason.toLowerCase().includes(filters.searchText.toLowerCase())
      );
    }

    setFilteredTransfers(filtered);
  }, [filters, transfers]);

  // 處理排序后的數據
  const sortedAndFilteredTransfers = useMemo(() => {
    let result = [...filteredTransfers];
    
    if (sortField && sortOrder) {
      result.sort((a, b) => {
        let aValue: any = a[sortField as keyof InternalTransfer];
        let bValue: any = b[sortField as keyof InternalTransfer];
        
        // 處理日期字段排序
        if (['requestedDate', 'approvedDate', 'executedDate', 'createdAt', 'updatedAt'].includes(sortField)) {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }
        
        // 處理數字字段排序
        if (['totalItems', 'totalQuantity', 'estimatedValue'].includes(sortField)) {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }
        
        // 處理字符串字段排序
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [filteredTransfers, sortField, sortOrder]);

  // 獲取狀態顏色
  const getStatusColor = (status: TransferStatus) => {
    switch (status) {
      case '待審批':
        return 'orange';
      case '已批準':
        return 'green';
      case '已拒絕':
        return 'red';
      default:
        return 'default';
    }
  };

  // 獲取狀態圖標
  const getStatusIcon = (status: TransferStatus) => {
    switch (status) {
      case '待審批':
        return <ClockCircleOutlined />;
      case '已批準':
        return <CheckCircleOutlined />;
      case '已拒絕':
        return <CloseCircleOutlined />;
      default:
        return <ExclamationCircleOutlined />;
    }
  };

  // 獲取優先級顏色
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'red';
      case 'high':
        return 'orange';
      case 'medium':
        return 'blue';
      case 'low':
        return 'green';
      default:
        return 'default';
    }
  };

  // 獲取優先級文本
  const getPriorityText = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return t('緊急');
      case 'high':
        return t('高');
      case 'medium':
        return t('中');
      case 'low':
        return t('低');
      default:
        return t('未知');
    }
  };

  // 處理新增轉移
  const handleAddTransfer = () => {
    setEditingTransfer(null);
    setFormVisible(true);
  };

  // 處理編輯轉移
  const handleEditTransfer = (transfer: InternalTransfer) => {
    setEditingTransfer(transfer);
    setFormVisible(true);
  };

  // 處理表單提交
  const handleFormSubmit = (formData: any) => {
    const submit = async () => {
      try {
        console.log('提交表單數據:', formData);
        if (editingTransfer) {
          console.log('更新轉移請求:', editingTransfer.id);
          const result = await apiService.updateInternalTransfer(editingTransfer.id, formData);
          console.log('更新結果:', result);
          message.success(t('轉移請求更新成功'));
        } else {
          console.log('創建新轉移請求');
          const result = await apiService.createInternalTransfer(formData);
          console.log('創建結果:', result);
          message.success(t('轉移請求創建成功'));
        }
        setFormVisible(false);
        // 重新加載數據
        await fetchTransfers();
      } catch (e) {
        console.error('表單提交錯誤:', e);
        if (e instanceof Error) {
          message.error(t('提交失敗: {{message}}', { message: e.message }));
        } else {
          message.error(t('提交失敗，請稍後重試'));
        }
      }
    };
    submit();
  };

  // 處理狀態變更
  const handleStatusChange = async (transferId: string, newStatus: TransferStatus) => {
    try {
      await apiService.updateInternalTransferStatus(transferId, newStatus);
      message.success(t('轉移請求狀態已更新為 {{newStatus}}', { newStatus }));
      await fetchTransfers();
    } catch (e) {
      console.error(e);
      message.error(t('更新狀態失敗'));
    }
  };

  // 處理刪除
  const handleDeleteTransfer = async (transferId: string) => {
    try {
      await apiService.deleteInternalTransfer(transferId);
      message.success(t('轉移請求刪除成功'));
      await fetchTransfers();
    } catch (e) {
      console.error(e);
      message.error(t('刪除失敗'));
    }
  };

  // 導出功能 - 客戶端CSV生成
  const handleExportExcel = () => {
    try {
      // 獲取過濾後的轉移請求數據
      const filteredTransfers = getFilteredTransfers();
      
      // 生成CSV內容
      const csvContent = generateCSVContent(filteredTransfers);
      
      // 創建Blob並下載
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `internal_transfers_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      message.success(t('內部轉移請求數據已成功導出'));
    } catch (error) {
      console.error('導出失敗:', error);
      message.error(t('導出失敗，請稍後重試'));
    }
  };

  // 生成CSV內容
  const generateCSVContent = (data: InternalTransfer[]) => {
    const headers = [
      '轉移編號', '標題', '源倉庫', '目標倉庫', '申請人', '申請時間', '審批人', '審批時間',
      '執行人', '執行時間', '狀態', '優先級', '轉移原因', '創建時間', '更新時間'
    ];

    const rows = data.map(item => [
      item.transferNumber || '',
      item.title || '',
      item.fromWarehouse || '',
      item.toWarehouse || '',
      item.requestedBy || '',
      item.requestedDate ? `\u200B${new Date(item.requestedDate).toISOString().split('T')[0]}` : '',
      item.approvedBy || '',
      item.approvedDate ? `\u200B${new Date(item.approvedDate).toISOString().split('T')[0]}` : '',
      item.executedBy || '',
      item.executedDate ? `\u200B${new Date(item.executedDate).toISOString().split('T')[0]}` : '',
      item.status || '',
      item.priority || '',
      item.reason || '',
      item.createdAt ? `\u200B${new Date(item.createdAt).toISOString().split('T')[0]}` : '',
      item.updatedAt ? `\u200B${new Date(item.updatedAt).toISOString().split('T')[0]}` : ''
    ]);

    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  // 獲取過濾後的轉移請求數據
  const getFilteredTransfers = () => {
    // 根據搜索文本和狀態過濾
    let filtered = sortedAndFilteredTransfers;
    
    if (filters.searchText) {
      filtered = filtered.filter((transfer: InternalTransfer) => 
        transfer.transferNumber?.toLowerCase().includes(filters.searchText.toLowerCase()) ||
        transfer.title?.toLowerCase().includes(filters.searchText.toLowerCase()) ||
        transfer.requestedBy?.toLowerCase().includes(filters.searchText.toLowerCase())
      );
    }
    
    if (filters.status) {
      filtered = filtered.filter((transfer: InternalTransfer) => transfer.status === filters.status);
    }
    
    if (filters.priority) {
      filtered = filtered.filter((transfer: InternalTransfer) => transfer.priority === filters.priority);
    }
    
    return filtered;
  };

  // 定義所有可用的列
  const allColumns = {
    transferNumber: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleColumnSort('transferNumber')}
        >
          {t('轉移編號')}
                          {getSortIcon('transferNumber')}
        </div>
      ),
      dataIndex: 'transferNumber',
      key: 'transferNumber',
      width: 130,
      sorter: true,
      render: (transferNumber: string, record: any) => (
        <div>
          <Text strong>{transferNumber}</Text>
          {record.items.some((item: any) => !item.isQuantityValid) && (
            <Tooltip title={t('存在庫存不足的商品')}>
              <WarningOutlined style={{ color: '#ff4d4f', marginLeft: 4 }} />
            </Tooltip>
          )}
        </div>
      ),
    },
    title: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleColumnSort('title')}
        >
          {t('標題')}
                          {getSortIcon('title')}
        </div>
      ),
      dataIndex: 'title',
      key: 'title',
      width: 240,
      sorter: true,
      ellipsis: {
        showTitle: false,
      },
      render: (title: string) => (
        <Tooltip placement="topLeft" title={title}>
          {title}
        </Tooltip>
      ),
    },
    direction: {
      title: t('轉移方向'),
      key: 'direction',
      width: 200,
      render: (_: any, record: any) => {
        const fromWarehouse = warehouses.find(w => w.value === record.fromWarehouse);
        const toWarehouse = warehouses.find(w => w.value === record.toWarehouse);
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Space>
              <HomeOutlined />
              <Text>{fromWarehouse?.label}</Text>
            </Space>
            <ArrowRightOutlined style={{ color: '#1890ff' }} />
            <Space>
              <HomeOutlined />
              <Text>{toWarehouse?.label}</Text>
            </Space>
          </div>
        );
      },
    },
    items: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleColumnSort('totalItems')}
        >
          {t('商品數量')}
                          {getSortIcon('totalItems')}
        </div>
      ),
      key: 'items',
      width: 100,
      sorter: true,
      render: (_: any, record: any) => (
        <div>
          <div>{record.totalItems} {t('種商品')}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {t('共')} {record.totalQuantity} {t('件')}
                            </Text>
        </div>
      ),
    },
    priority: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleColumnSort('priority')}
        >
          {t('優先級')}
                          {getSortIcon('priority')}
        </div>
      ),
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      sorter: true,
      render: (priority: string) => (
        <Tag color={getPriorityColor(priority)}>
          {getPriorityText(priority)}
        </Tag>
      ),
    },
    status: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleColumnSort('status')}
        >
          {t('狀態')}
                          {getSortIcon('status')}
        </div>
      ),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      sorter: true,
      render: (status: TransferStatus) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {statusLabel(status)}
        </Tag>
      ),
    },
    requestedBy: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleColumnSort('requestedBy')}
        >
          {t('申請人')}
                          {getSortIcon('requestedBy')}
        </div>
      ),
      key: 'requestedBy',
      width: 100,
      sorter: true,
      render: (_: any, record: any) => (
        <Space>
          <Avatar size="small" icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: 500 }}>{record.requestedBy}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {dayjs(record.requestedDate).format('MM-DD HH:mm')}
            </Text>
          </div>
        </Space>
      ),
    },
    actions: {
      title: t('操作'),
      key: 'actions',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: any) => (
        <Space size="small" direction="vertical">
          <Space size="small">
            <Tooltip title={t('查看詳情')}>
              <Button
                type="default"
                icon={<EyeOutlined />}
                onClick={() => {
                  setSelectedTransfer(record);
                  setDrawerVisible(true);
                }}
                size="small"
              >
                {t('詳情')}
                                        </Button>
            </Tooltip>
            <Tooltip title={t('編輯')}>
              <Button
                type="default"
                icon={<EditOutlined />}
                onClick={() => handleEditTransfer(record)}
                size="small"
              >
                {t('編輯')}
                                        </Button>
            </Tooltip>
            <Popconfirm
              title={t('確認刪除此轉移請求？')}
              okText={t('確認')}
              cancelText={t('取消')}
              onConfirm={() => handleDeleteTransfer(record.id)}
            >
              <Button
                type="default"
                danger
                icon={<DeleteOutlined />}
                size="small"
              >
                {t('刪除')}
                                        </Button>
            </Popconfirm>
          </Space>
          <Select
            size="small"
            value={record.status}
            onChange={(value) => handleStatusChange(record.id, value)}
            style={{ width: 100 }}
          >
            <Option value="待審批">{t('待審批')}</Option>
            <Option value="已批準">{t('已批準')}</Option>
            <Option value="已拒絕">{t('已拒絕')}</Option>
          </Select>
        </Space>
      ),
    },
  };

  // 列設置菜單
  const columnSettingsMenu = (
    <div style={{ 
      background: '#fff', 
      borderRadius: '2px', 
      boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08), 0 9px 28px 8px rgba(0,0,0,.05)',
      padding: '4px 0',
      minWidth: '160px'
    }}>
      <div style={{ padding: '5px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <Text strong>{t('列設置')}</Text>
      </div>
      <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '4px 0' }}>
        {Object.entries(allColumns).map(([key, column]) => (
          <div 
            key={key}
            style={{ padding: '5px 12px', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              handleColumnVisibilityChange(key, !(columnSettings[key]?.visible ?? true));
            }}
          >
            <Checkbox
              checked={columnSettings[key]?.visible ?? true}
              onChange={(e) => {
                e.stopPropagation();
                handleColumnVisibilityChange(key, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {typeof column.title === 'string' ? column.title : 
               key === 'transferNumber' ? t('轉移編號') :
               key === 'title' ? t('標題') :
               key === 'direction' ? t('轉移方向') :
               key === 'items' ? t('商品數量') :
               key === 'priority' ? t('優先級') :
               key === 'status' ? t('狀態') :
               key === 'requestedBy' ? t('申請人') :
               key === 'actions' ? t('操作') : key}
            </Checkbox>
          </div>
        ))}
      </div>
    </div>
  );

  // 根據列配置生成顯示的列
  const columns = Object.entries(columnSettings)
    .filter(([_, config]: [string, any]) => config.visible)
    .sort(([, a]: [string, any], [, b]: [string, any]) => a.order - b.order)
    .map(([key]) => {
      const column = allColumns[key as keyof typeof allColumns];
      if (!column) {
        console.warn(`Missing column definition for key: ${key}`);
      }
      return column;
    })
    .filter(Boolean);

  // 內部轉移表支持拖拽列排序（禁用 actions）
  const { columns: draggableColumns, order: transferOrder } = useDraggableColumns<InternalTransfer>({
    columns: columns as any,
    disableDragKeys: ['actions'],
    storageKey: 'internal-transfer-table',
  });

  useEffect(() => {
    if (!transferOrder || transferOrder.length === 0) return;
    setColumnSettings((prev: any) => {
      const next: any = { ...prev };
      transferOrder.forEach((k: string, idx: number) => {
        if (next[k]) next[k] = { ...next[k], order: idx };
      });
      Object.keys(next).forEach((k) => {
        if (!transferOrder.includes(k)) {
          const o = next[k]?.order ?? 9999;
          next[k] = { ...next[k], order: Math.max(transferOrder.length, o) };
        }
      });
      try {
        localStorage.setItem('internal-transfer-columns-config', JSON.stringify(next));
        localStorage.setItem('internal-transfer-columns-version', '2024-01-v2');
      } catch {}
      return next;
    });
  }, [transferOrder]);

  return (
    <div style={{ padding: '24px' }}>
      {/* 標題和展開收起按鈕 */}
      <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <Title level={2} style={{ marginBottom: 0 }}>{t('內部轉移請求')}</Title>
          <Text type="secondary">{t('管理倉庫間或庫位間的庫存轉移請求，包括申請、審批、執行等完整流程。')}</Text>
        </div>
        <Button
          type="text"
          icon={collapsed ? <DownOutlined /> : <UpOutlined />}
          onClick={toggleCollapsed}
          style={{ fontSize: 16 }}
        />
      </div>

      {/* KPI 儀表板 */}
      {!collapsed && (
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col span={6}>
            <Card hoverable>
              <Statistic
                title={t('總請求數')}
                value={statistics.total}
                prefix={<SwapOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card hoverable>
              <Statistic
                title={t('待審批')}
                value={statistics.pending}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card hoverable>
              <Statistic
                title={t('已批準')}
                value={statistics.approved}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card hoverable>
              <Statistic
                title={t('已拒絕')}
                value={statistics.rejected}
                prefix={<CloseCircleOutlined />}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* 操作欄 */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Input
              placeholder={t('搜尋轉移編號、標題、原因...')}
              prefix={<SearchOutlined />}
              value={filters.searchText}
              onChange={(e) => setFilters({ ...filters, searchText: e.target.value })}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder={t('狀態篩選')}
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              allowClear
            >
              <Option value="待審批">{t('待審批')}</Option>
              <Option value="已批準">{t('已批準')}</Option>
              <Option value="已拒絕">{t('已拒絕')}</Option>
            </Select>
          </Col>
          <Col span={6}>
            <Select
              placeholder={t('倉庫篩選')}
              style={{ width: '100%' }}
              value={filters.warehouse}
              onChange={(value) => setFilters({ ...filters, warehouse: value })}
              allowClear
            >
              {warehouses.map(warehouse => (
                <Option key={warehouse.value} value={warehouse.value}>
                  {warehouse.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <Space style={{ float: 'right' }}>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  setLoading(true);
                  setTimeout(() => setLoading(false), 1000);
                  message.success(t('數據已刷新'));
                }}
                loading={loading}
              >
                {t('刷新數據')}
                                            </Button>
              <Button
                type="default"
                icon={<FileExcelOutlined />}
                onClick={handleExportExcel}
              >
                {t('導出')}
                                            </Button>
              <Dropdown 
                overlay={columnSettingsMenu} 
                trigger={['click']}
                open={columnSettingsVisible}
                onOpenChange={setColumnSettingsVisible}
              >
                <Button icon={<SettingOutlined />}>
                  {t('列設置')}
                                                  </Button>
              </Dropdown>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddTransfer}
              >
                {t('新增轉移請求')}
                                            </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 轉移請求表格 */}
      <Card>
        <Table
          columns={draggableColumns as any}
          dataSource={sortedAndFilteredTransfers}
          rowKey="id"
          loading={loading}
          pagination={{
            total: sortedAndFilteredTransfers.length,
            //pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100, 500],
            showTotal: (total, range) =>
              t('顯示 {{from}}-{{to}} 項，共 {{total}} 項記錄', { from: range[0], to: range[1], total }),
          }}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* 轉移詳情抽屜 */}
      <Drawer
        title={t('轉移請求詳情')}
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={800}
      >
        {selectedTransfer && (
          <div>
            {/* 基本信息 */}
            <Card title={t('基本信息')} style={{ marginBottom: 16 }}>
              <Descriptions column={2} bordered>
                <Descriptions.Item label={t('轉移編號')}>{selectedTransfer.transferNumber}</Descriptions.Item>
                <Descriptions.Item label={t('標題')}>{selectedTransfer.title}</Descriptions.Item>
                <Descriptions.Item label={t('申請人')}>{selectedTransfer.requestedBy}</Descriptions.Item>
                <Descriptions.Item label={t('申請時間')}>
                  {dayjs(selectedTransfer.requestedDate).format('YYYY-MM-DD HH:mm:ss')}
                </Descriptions.Item>
                <Descriptions.Item label={t('優先級')}>
                  <Tag color={getPriorityColor(selectedTransfer.priority)}>
                    {getPriorityText(selectedTransfer.priority)}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('狀態')}>
                  <Tag color={getStatusColor(selectedTransfer.status)}>
                    {selectedTransfer.status}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('轉移原因')} span={2}>
                  {selectedTransfer.reason}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* 轉移信息 */}
            <Card title={t('轉移信息')} style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Card size="small" title={t('源倉庫')}>
                    <div style={{ textAlign: 'center' }}>
                      <HomeOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                      <div style={{ fontWeight: 500 }}>
                        {warehouses.find(w => w.value === selectedTransfer.fromWarehouse)?.label}
                      </div>
                    </div>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card size="small" title={t('目標倉庫')}>
                    <div style={{ textAlign: 'center' }}>
                      <HomeOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                      <div style={{ fontWeight: 500 }}>
                        {warehouses.find(w => w.value === selectedTransfer.toWarehouse)?.label}
                      </div>
                    </div>
                  </Card>
                </Col>
              </Row>
            </Card>

            {/* 商品清單 */}
            <Card title={t('轉移商品清單')}>
              <Table
                dataSource={selectedTransfer.items}
                rowKey="id"
                size="small"
                pagination={false}
                columns={[
                  {
                    title: t('商品'),
                    key: 'product',
                    render: (_, item) => (
                      <div>
                        <div style={{ fontWeight: 500 }}>{item.productName}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{item.productNameCN}</div>
                        <div style={{ fontSize: '12px', color: '#999' }}>{t('SKU:')} {item.sku}</div>
                      </div>
                    ),
                  },
                  {
                    title: t('轉移數量'),
                    key: 'quantity',
                    width: 100,
                    render: (_, item) => (
                      <div>
                        <Text strong>{item.quantity} {item.unit}</Text>
                        {!item.isQuantityValid && (
                          <div style={{ color: '#ff4d4f', fontSize: '12px' }}>
                            {t('可用:')} {item.availableQuantity}
                          </div>
                        )}
                      </div>
                    ),
                  },
                  {
                    title: t('庫位'),
                    key: 'location',
                    width: 150,
                    render: (_, item) => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text>{shortLocation(item.fromLocation)}</Text>
                        <ArrowRightOutlined style={{ color: '#1890ff' }} />
                        <Text>{shortLocation(item.toLocation)}</Text>
                      </div>
                    ),
                  },
                  {
                    title: t('狀態'),
                    key: 'status',
                    width: 80,
                    render: (_, item) => (
                      item.isQuantityValid ? 
                        <Tag color="green">{t('正常')}</Tag> : 
                        <Tag color="red">{t('庫存不足')}</Tag>
                    ),
                  },
                ]}
              />
            </Card>

            {/* 審批信息 */}
            {(selectedTransfer.approvedBy || selectedTransfer.executedBy) && (
              <Card title={t('處理記錄')} style={{ marginTop: 16 }}>
                <Steps direction="vertical" size="small">
                  <Step
                    title={t('申請提交')}
                    description={`${selectedTransfer.requestedBy} • ${dayjs(selectedTransfer.requestedDate).format('YYYY-MM-DD HH:mm:ss')}`}
                    status="finish"
                    icon={<UserOutlined />}
                  />
                  {selectedTransfer.approvedBy && (
                    <Step
                      title={t('審批通過')}
                      description={`${selectedTransfer.approvedBy} • ${dayjs(selectedTransfer.approvedDate).format('YYYY-MM-DD HH:mm:ss')}`}
                      status="finish"
                      icon={<CheckCircleOutlined />}
                    />
                  )}
                  {selectedTransfer.executedBy && (
                    <Step
                      title={t('執行完成')}
                      description={`${selectedTransfer.executedBy} • ${dayjs(selectedTransfer.executedDate).format('YYYY-MM-DD HH:mm:ss')}`}
                      status="finish"
                      icon={<BoxPlotOutlined />}
                    />
                  )}
                </Steps>
              </Card>
            )}
          </div>
        )}
      </Drawer>

      {/* 新增/編輯表單 */}
      <InternalTransferForm
        visible={formVisible}
        transfer={editingTransfer}
        warehouses={warehouses}
        products={products}
        customers={customers}
        onSubmit={handleFormSubmit}
        onCancel={() => setFormVisible(false)}
      />
    </div>
  );
};

export default InternalTransferPage;